import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { 
  createChart, 
  IChartApi, 
  ISeriesApi, 
  CandlestickData, 
  LineData, 
  UTCTimestamp,
  LineSeries,
  AreaSeries,
  HistogramSeries,
  CandlestickSeries
} from 'lightweight-charts';
import { Eye, EyeOff, Play, Pause, Settings, X, ChevronRight, RotateCcw } from 'lucide-react';
import ChartTooltip from './ChartTooltip';
import ChartOhlcReadout from './ChartOhlcReadout';
import ChartCandleCountdown from './ChartCandleCountdown';
import { CHART_OVERLAY_HOST_ID } from '../constants/chartOverlay';
import Loader from './Loader';
import { formatPriceWithAssetDecimals, getAssetDecimalPlaces } from '../utils/priceFormatter';
import { detectOrderType } from '../utils/orderTypeDetector';
import { binanceService } from '../services/binanceService';
import liveChartStream, { LiveKline } from '../services/liveChartStream';
import { useTheme } from '../contexts/ThemeContext';
import { useAITrading } from '../contexts/AITradingContext';
import { useAuth } from '../contexts/AuthContext';
import { useAccountType } from '../contexts/AccountTypeContext';
import { useBackendAITrading } from '../contexts/BackendAITradingContext';
import { useAccount } from '../contexts/AccountContext';
import { useAIStrategyPersistenceContext } from '../contexts/AIStrategyPersistenceContext';
import { usePriceInput } from '../contexts/PriceInputContext';
import { useOrderContext } from '../contexts/OrderContext';
import { usePositionContext } from '../contexts/PositionContext';
import { useChartPositionContext } from '../contexts/ChartPositionContext';
import { useTradingError } from '../contexts/TradingErrorContext';
import TradingErrorModal from './TradingErrorModal';
import ChartPositionManager from './ChartPositionManager';
import ChartingTools, { DrawingTool, DrawingElement } from './ChartingTools';
import AITradingSettings from './ai-trading/AITradingSettings';
import { useUnifiedAITrading } from '../hooks/useUnifiedAITrading';
import NumberInput from './NumberInput';

interface TradingChartProps {
  symbol: string;
  timeframe: string;
  chartType: string;
  onPriceUpdate?: (price: string, change: string, isPositive: boolean) => void;
  onKlinePriceUpdate?: (formattedPrice: string, closePrice: number, volume: number) => void;
}

interface BinanceKline {
  [0]: number; // Open time
  [1]: string; // Open
  [2]: string; // High
  [3]: string; // Low
  [4]: string; // Close
  [5]: string; // Volume
  [6]: number; // Close time
  [7]: string; // Quote asset volume
  [8]: number; // Number of trades
  [9]: string; // Taker buy base asset volume
  [10]: string; // Taker buy quote asset volume
  [11]: string; // Ignore
}

// Candlestick series consume the full OHLC bar; line/area/bar/histogram series expect
// { time, value }.
const toSeriesPoint = (candle: CandlestickData, chartType: string): any =>
  chartType === 'candlestick' ? candle : { time: candle.time, value: candle.close };

const toSeriesData = (candles: CandlestickData[], chartType: string): any[] =>
  chartType === 'candlestick' ? candles : candles.map((candle) => toSeriesPoint(candle, chartType));

// Bar duration in seconds, used to roll the live candle over when its period ends.
// Keys are matched case-sensitively so '1M' (month) cannot collide with '1m' (minute);
// month and year intervals have no fixed length, so they are absent and fall back to the
// periodic REST refresh.
const TIMEFRAME_SECONDS: Record<string, number> = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1H': 3600,
  '4H': 14400,
  '6H': 21600,
  '12H': 43200,
  '1D': 86400,
  '1W': 604800,
  '1h': 3600,
  '4h': 14400,
  '6h': 21600,
  '12h': 43200,
  '1d': 86400,
  '1w': 604800,
};

const getTimeframeSeconds = (timeframe: string): number | null =>
  TIMEFRAME_SECONDS[timeframe] ?? null;

/**
 * Width of a candle plus its gap on a fresh chart, and after a reset.
 *
 * One zoom step out from where it used to sit, which fits noticeably more history on screen
 * without the bars getting too thin to read a wick from.
 */
const DEFAULT_BAR_SPACING = 5;
const DEFAULT_RIGHT_OFFSET = 5;

/**
 * Bar times are UTC seconds, and lightweight-charts renders them in UTC unless it is told
 * otherwise, so a trader in Mumbai read every axis label and crosshair five and a half hours
 * behind their own clock. `Date` converts to the machine's timezone on its own; these
 * formatters simply route the library's labels through it.
 */
const localBarDate = (time: unknown): Date => new Date(Number(time) * 1000);

/** The crosshair's full date and time, in the user's timezone. */
const formatCrosshairTime = (time: unknown): string =>
  localBarDate(time).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

/**
 * One tick mark on the time axis. The library says how much of the date it wants shown by
 * the mark's type — a new year, a new month, a new day, or another time within one — and
 * only that part is rendered, so the axis stays as sparse as it was before.
 */
const formatAxisTickMark = (time: unknown, tickMarkType: number): string => {
  const date = localBarDate(time);

  switch (tickMarkType) {
    case 0: // Year
      return String(date.getFullYear());
    case 1: // Month
      return date.toLocaleString(undefined, { month: 'short' });
    case 2: // DayOfMonth
      return date.toLocaleString(undefined, { day: 'numeric', month: 'short' });
    case 4: // TimeWithSeconds
      return date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    default: // Time
      return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  }
};

// 1000 is Binance's maximum bars per klines request. Older bars beyond this are paged in
// on demand as the user scrolls back, in blocks of the same size.
const HISTORY_PAGE_SIZE = 1000;

// How close to the oldest loaded bar the viewport must come before the next page is
// requested, in bars. Roughly a screen's worth, so history arrives before the edge is hit.
const BACKFILL_TRIGGER_BARS = 100;

// Upper bound on retained bars, so a session left open for days cannot grow without limit
// while still leaving room for a lot of paged-in history.
const MAX_RETAINED_BARS = 20000;

const TradingChart: React.FC<TradingChartProps> = ({
  symbol,
  timeframe,
  chartType,
  onPriceUpdate,
  onKlinePriceUpdate,
}) => {
  // Volume bar configuration - set to false to disable volume bars
  const SHOW_VOLUME_BARS = false;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const [mainSeries, setMainSeries] = useState<ISeriesApi<"Candlestick"> | null>(null);
  const [isChartDisposed, setIsChartDisposed] = useState(false);
  const greenVolumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const redVolumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  // True when the newest candle has been scrolled off the right edge, which is when the
  // jump-to-latest button is offered. Mirrored in a ref so the range subscription can skip
  // re-rendering while the flag is unchanged; it fires on every pan and zoom frame.
  const [isScrolledBack, setIsScrolledBack] = useState(false);
  const isScrolledBackRef = useRef(false);
  // inFlight stops overlapping pages while scrolling; exhausted records that Binance has no
  // bars older than the ones already loaded, so we stop asking for the same empty page.
  const backfillStateRef = useRef({ inFlight: false, exhausted: false });
  const [isLiveStreamConnected, setIsLiveStreamConnected] = useState(false);
  const liveStreamConnectedRef = useRef(false);
  // Bar time the crosshair is over, or null when the pointer is off the chart; the OHLC
  // readout falls back to the newest bar then. Held in a ref rather than state because the
  // crosshair fires on every pointer move, and re-rendering this component that often would
  // undo the whole reason the candles live in refs. The readout polls it instead.
  const hoveredBarTimeRef = useRef<number | null>(null);
  const [isSymbolChanging, setIsSymbolChanging] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [currentPrice, setCurrentPrice] = useState<string>('0.00');
  const [priceChange, setPriceChange] = useState<string>('+0.00%');
  const [isPositiveChange, setIsPositiveChange] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('');
  // The datasets are held in refs rather than state: a tick arrives about once a second,
  // and any state update here would re-render, rebuild the chart and discard the user's
  // zoom/pan. The series is driven imperatively via setData()/update() instead.
  const chartDataRef = useRef<CandlestickData[]>([]);
  const greenVolumeDataRef = useRef<LineData[]>([]);
  const redVolumeDataRef = useRef<LineData[]>([]);
  // Volume by bar time. The two arrays above are split by candle direction because that is
  // what the histogram series need to colour themselves, which makes reading a single bar's
  // volume a search of both. This is the same numbers keyed for lookup, and unlike those it
  // is maintained from the live stream too, so the forming bar has a volume as well.
  const volumeByTimeRef = useRef<Map<number, number>>(new Map());
  // symbol|timeframe of the data currently loaded into the series. A mismatch means the
  // next fetch has to replace the whole series instead of appending to it.
  const loadedDatasetKeyRef = useRef<string>('');
  const chartTypeRef = useRef(chartType);
  chartTypeRef.current = chartType;
  const [prevSymbol, setPrevSymbol] = useState(symbol);
  const [showPositionOverlay, setShowPositionOverlay] = useState(true);
  const [selectedDrawingTool, setSelectedDrawingTool] = useState<DrawingTool>('none');
  const [drawingElements, setDrawingElements] = useState<DrawingElement[]>([]);
  const [showAISettings, setShowAISettings] = useState(false);
  
  // Loading states for chart actions
  const [isBuyLoading, setIsBuyLoading] = useState(false);
  const [isSellLoading, setIsSellLoading] = useState(false);
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);
  const [isAILoading, setIsAILoading] = useState(false);
  
  // Use unified AI trading hook
  const {
    aiState,
    handlers,
    currentFirestoreStrategy: aiCurrentFirestoreStrategy,
    currentAssetConfig: aiCurrentAssetConfig,
    assetAIConfig,
    refreshTrigger
  } = useUnifiedAITrading(
    symbol,
    () => setShowAISettings(true),
    () => setShowAISettings(false)
  );
  
  // Debug logging for refreshTrigger
  // No logging needed
  const [isHolding, setIsHolding] = useState(false);
  const [showDateTime, setShowDateTime] = useState(false);
  const [buyOrderType, setBuyOrderType] = useState<'limit' | 'stop' | null>(null);
  const [sellOrderType, setSellOrderType] = useState<'limit' | 'stop' | null>(null);
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Use price input context
  const { 
    buyPrice, 
    sellPrice, 
    showBuyInput, 
    showSellInput, 
    setBuyPrice, 
    setSellPrice, 
    fillBuyPrice, 
    fillSellPrice, 
    setBuyPriceInputVisible, 
    setSellPriceInputVisible 
  } = usePriceInput();
  const fetchInProgressRef = useRef<boolean>(false);

  // Initialize prevSymbol on mount to prevent false symbol change detection
  useEffect(() => {
    if (!prevSymbol && symbol) {
      setPrevSymbol(symbol);
    }
  }, [symbol, prevSymbol]);

  // Show date and time after 2 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowDateTime(true);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Update buy order type when buy price or current price changes
  useEffect(() => {
    if (showBuyInput && buyPrice && currentPrice) {
      const inputPrice = parseFloat(buyPrice);
      const currentPriceNum = parseFloat(currentPrice.replace(/[,$]/g, ''));
      if (!isNaN(inputPrice) && !isNaN(currentPriceNum)) {
        const orderTypeDetection = detectOrderType('buy', inputPrice, currentPriceNum);
        setBuyOrderType(orderTypeDetection.orderType);
      }
    } else {
      setBuyOrderType(null);
    }
  }, [showBuyInput, buyPrice, currentPrice]);

  // Update sell order type when sell price or current price changes
  useEffect(() => {
    if (showSellInput && sellPrice && currentPrice) {
      const inputPrice = parseFloat(sellPrice);
      const currentPriceNum = parseFloat(currentPrice.replace(/[,$]/g, ''));
      if (!isNaN(inputPrice) && !isNaN(currentPriceNum)) {
        const orderTypeDetection = detectOrderType('sell', inputPrice, currentPriceNum);
        setSellOrderType(orderTypeDetection.orderType);
      }
    } else {
      setSellOrderType(null);
    }
  }, [showSellInput, sellPrice, currentPrice]);

  // Live prices are read straight from binanceService below rather than through
  // useBinanceMarketData: the hook stores every websocket batch in React state, which
  // would re-render this component around ten times a second for no benefit.
  
  // Use theme context
  const { theme } = useTheme();

  // Use AI trading context for background trading (legacy for compatibility)
  const { 
    updatePriceForAI,
    getStrategy, 
    updateStrategy,
    updateSignal,
    updateConfig,
    startStrategy,
    stopStrategy,
    closeAllAIPositions
  } = useAITrading();

  // Use Backend AI trading context for persistent settings
  const {
    isAIEnabled,
    strategies,
    activeStrategies,
    enableAI,
    disableAI,
    addStrategy,
    activateStrategy,
    deactivateStrategy,
    // New per-asset methods
    getAssetAIConfig,
    isAssetAIEnabled,
    setAssetAIEnabled,
    stopAssetAITrading, // NEW: Comprehensive stop method
    saveAssetStrategy
  } = useBackendAITrading();
  
  // Import AI strategy persistence for saving Firestore updates
  const { saveStrategy } = useAIStrategyPersistenceContext();
  
  // Use order and position contexts for trading
  const { executeOrder } = useOrderContext();
  const { addOpenPosition } = usePositionContext();
  const { chartPositions, syncWithPendingOrders } = useChartPositionContext();
  const { error: tradingError, hideError, retryOrder, setOnRetry } = useTradingError();
  const { getAccountBalance } = useAccount();
  const { accountType } = useAccountType();
  
  // Get account-specific balance data
  const accountBalance = getAccountBalance(accountType);

  // Handle Buy button click
  const handleBuyClick = useCallback(async () => {
    if (isBuyLoading) return; // Prevent multiple clicks
    
    setIsBuyLoading(true);
    try {
      let orderDetails;
      
      // Calculate trade amount as 20% of total balance
      const currentBalance = accountBalance.totalBalance;
      const tradeAmountUSD = currentBalance * 0.20; // 20% of balance
      const currentPriceNum = parseFloat(currentPrice.replace(/[,$]/g, ''));
      const amount = Math.max(1.10, tradeAmountUSD / currentPriceNum); // Minimum 1.10 quantity
      
      // If input is visible, determine order type based on price comparison
      if (showBuyInput && buyPrice) {
        const inputPrice = parseFloat(buyPrice);
        const orderTypeDetection = detectOrderType('buy', inputPrice, currentPriceNum);
        
        orderDetails = {
          symbol: symbol,
          side: 'buy' as const,
          type: orderTypeDetection.orderType as 'limit' | 'stop',
          price: orderTypeDetection.isStopOrder ? currentPriceNum : inputPrice, // For stop orders, use current price as execution price
          stopPrice: orderTypeDetection.isStopOrder ? inputPrice : undefined, // For stop orders, use input price as stop price
          amount: amount,
          leverage: 100, // 100x leverage
        };
      } else {
        // Execute market order
        orderDetails = {
          symbol: symbol,
          side: 'buy' as const,
          type: 'market' as const,
          price: currentPriceNum,
          amount: amount,
          leverage: 100, // 100x leverage
        };
      }

      // Set up retry functionality
      setOnRetry(() => () => handleBuyClick());
      
      // Execute the order
      const result = await executeOrder(orderDetails);
      
      if (result.success) {
        // Reset input state after successful order
        if (showBuyInput) {
          setBuyPriceInputVisible(false);
        }
      }
      // Error handling is now done in the OrderContext via TradingErrorContext
    } catch (error) {
      console.error('Error executing buy order:', error);
    } finally {
      setIsBuyLoading(false);
    }
  }, [currentPrice, symbol, executeOrder, showBuyInput, buyPrice, setBuyPriceInputVisible, setOnRetry, accountBalance, accountType, isBuyLoading]);

  // Handle Sell button click
  const handleSellClick = useCallback(async () => {
    if (isSellLoading) return; // Prevent multiple clicks
    
    setIsSellLoading(true);
    try {
      let orderDetails;
      
      // Calculate trade amount as 20% of total balance
      const currentBalance = accountBalance.totalBalance;
      const tradeAmountUSD = currentBalance * 0.20; // 20% of balance
      const currentPriceNum = parseFloat(currentPrice.replace(/[,$]/g, ''));
      const amount = Math.max(1.10, tradeAmountUSD / currentPriceNum); // Minimum 1.10 quantity
      
      // If input is visible, determine order type based on price comparison
      if (showSellInput && sellPrice) {
        const inputPrice = parseFloat(sellPrice);
        const orderTypeDetection = detectOrderType('sell', inputPrice, currentPriceNum);
        
        orderDetails = {
          symbol: symbol,
          side: 'sell' as const,
          type: orderTypeDetection.orderType as 'limit' | 'stop',
          price: orderTypeDetection.isStopOrder ? currentPriceNum : inputPrice, // For stop orders, use current price as execution price
          stopPrice: orderTypeDetection.isStopOrder ? inputPrice : undefined, // For stop orders, use input price as stop price
          amount: amount,
          leverage: 100, // 100x leverage
        };
      } else {
        // Execute market order
        orderDetails = {
          symbol: symbol,
          side: 'sell' as const,
          type: 'market' as const,
          price: currentPriceNum,
          amount: amount,
          leverage: 100, // 100x leverage
        };
      }

      // Set up retry functionality
      setOnRetry(() => () => handleSellClick());
      
      // Execute the order
      const result = await executeOrder(orderDetails);
      
      if (result.success) {
        // Reset input state after successful order
        if (showSellInput) {
          setSellPriceInputVisible(false);
        }
      }
      // Error handling is now done in the OrderContext via TradingErrorContext
    } catch (error) {
      console.error('Error executing sell order:', error);
    } finally {
      setIsSellLoading(false);
    }
  }, [currentPrice, symbol, executeOrder, showSellInput, sellPrice, setSellPriceInputVisible, setOnRetry, accountBalance, accountType, isSellLoading]);

  // Handle Buy button hold
  const handleBuyHold = useCallback(() => {
    setIsHolding(true);
    holdTimeoutRef.current = setTimeout(() => {
      fillBuyPrice(currentPrice.replace(/[,$]/g, ''));
      setIsHolding(false);
    }, 500); // 500ms hold time
  }, [currentPrice, fillBuyPrice]);

  // Handle Sell button hold
  const handleSellHold = useCallback(() => {
    setIsHolding(true);
    holdTimeoutRef.current = setTimeout(() => {
      fillSellPrice(currentPrice.replace(/[,$]/g, ''));
      setIsHolding(false);
    }, 500); // 500ms hold time
  }, [currentPrice, fillSellPrice]);

  // Handle button release
  const handleButtonRelease = useCallback(() => {
    setIsHolding(false);
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
  }, []);

  // Close input functions
  const closeBuyInput = useCallback(() => {
    setBuyPriceInputVisible(false);
  }, [setBuyPriceInputVisible]);

  const closeSellInput = useCallback(() => {
    setSellPriceInputVisible(false);
  }, [setSellPriceInputVisible]);

  // Handle drawing tool selection
  const handleDrawingToolSelect = useCallback((tool: DrawingTool) => {
    setSelectedDrawingTool(tool);
  }, []);

  // Handle drawing completion
  const handleDrawingComplete = useCallback((element: DrawingElement) => {
    setDrawingElements(prev => [...prev, element]);
  }, []);

  // Handle clear all drawings
  const handleClearAllDrawings = useCallback(() => {
    setDrawingElements([]);
  }, []);

  // Get current asset configuration from context
  const currentAssetConfig = getStrategy(symbol);

  // AI state is now handled by the unified hook
  
  // For backward compatibility, also check local running state
  const isAIRunning = aiState.isRunning || aiCurrentAssetConfig.isRunning;
  
  // Force re-render when refreshTrigger changes
  useEffect(() => {
    // No logging needed
  }, [refreshTrigger, symbol]);

  // Use unified AI click handler
  const handleAIClick = handlers.handleStartStop;

  // Handle strategy change
  const handleStrategyChange = useCallback(async (strategyId: string) => {
    try {
      // If strategyId is empty, it means we're removing the strategy
      if (!strategyId) {
        await handlers.handleRemoveStrategy();
        return;
      }
      
      // Update legacy strategy for backward compatibility
      updateStrategy(symbol, { strategy: strategyId });
    } catch (error) {
      console.error(`❌ TradingChart: Error handling strategy change for ${symbol}:`, error);
    }
  }, [symbol, updateStrategy, handlers]);

  // Handle config update
  const handleConfigUpdate = useCallback((config: any) => {
    updateConfig(symbol, config);
  }, [symbol, updateConfig]);

  // Handle signal update
  const handleSignalUpdate = useCallback((signal: 'buy' | 'sell' | null) => {
    updateSignal(symbol, signal);
  }, [symbol, updateSignal]);

  // Handle strategy settings click
  const handleStrategySettingsClick = useCallback(async () => {
    if (isSettingsLoading) return; // Prevent multiple clicks
    
    setIsSettingsLoading(true);
    try {
      setShowAISettings(true);
      // Brief delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 300));
    } finally {
      setIsSettingsLoading(false);
    }
  }, [isSettingsLoading]);

  // Handle AI Start/Stop with loading state
  const handleAIStartStop = useCallback(async () => {
    if (isAILoading) return; // Prevent multiple clicks
    
    setIsAILoading(true);
    try {
      if (isAIRunning) {
        await handlers.handleStopAI();
      } else {
        await handlers.handleStartAI();
      }
    } catch (error) {
      console.error('Error handling AI start/stop:', error);
    } finally {
      setIsAILoading(false);
    }
  }, [isAILoading, isAIRunning, handlers]);


  // As in TradingView, the price scale tracks the bars currently in view. Leaving
  // autoScale off froze the price range, so a moving price would drift out of sight and
  // the live candle looked stuck.
  const applyPriceScaleConfig = useCallback(() => {
    if (!chartRef.current) return;
    
    try {
      const priceScale = chartRef.current.priceScale('right');
      priceScale.applyOptions({
        autoScale: true,
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      });
    } catch (error) {
      console.warn('Error applying price scale config:', error);
    }
  }, []);

  // Reset zoom/pan back to the default view, matching TradingView's reset action.
  const resetChart = useCallback(() => {
    if (!chartRef.current || chartDataRef.current.length === 0) return;

    try {
      const timeScale = chartRef.current.timeScale();
      timeScale.applyOptions({
        barSpacing: DEFAULT_BAR_SPACING,
        minBarSpacing: 0.1,
      });
      timeScale.resetTimeScale();
      chartRef.current.priceScale('right').applyOptions({ autoScale: true });
    } catch (error) {
      console.warn('Error resetting chart:', error);
    }
  }, []);

  // Show the loader as soon as loading starts, and hide it a beat after loading finishes
  // so a fast response does not produce a visible flash.
  //
  // showLoader is deliberately not a dependency: it is the state this effect sets, so
  // listing it re-ran the effect on its own output and restarted the hide timer.
  useEffect(() => {
    if (isLoading) {
      setShowLoader(true);
      return;
    }

    const timeoutId = setTimeout(() => setShowLoader(false), 500);
    return () => clearTimeout(timeoutId);
  }, [isLoading]);

  // Add keyboard shortcut for reset (like TradingView)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Reset chart on Home key or Ctrl+R
      if (event.key === 'Home' || (event.ctrlKey && event.key === 'r')) {
        event.preventDefault();
        resetChart();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [resetChart]);

  // Determine if symbol is crypto
  const isCrypto = symbol.includes('USDT') || symbol.includes('BTC') || symbol.includes('ETH') || 
                   symbol.includes('SOL') || symbol.includes('ADA') || symbol.includes('DOT') || 
                   symbol.includes('LINK') || symbol.includes('DOGE') || symbol.includes('XRP') ||
                   symbol.includes('BCH') || symbol.includes('DASH') || symbol.includes('LTC') ||
                   symbol.includes('XLM') || symbol.includes('XMR');

  // Convert timeframe to Binance interval
  const getBinanceInterval = useCallback((tf: string): string => {
    // Tested before lower-casing, otherwise '1M' (month) is indistinguishable from
    // '1m' (minute) and monthly charts silently load one-minute bars.
    if (tf === '1M') return '1M';

    const normalizedTf = tf.toLowerCase();
    
    switch (normalizedTf) {
      case '1m': return '1m';
      case '5m': return '5m';
      case '15m': return '15m';
      case '30m': return '30m';
      case '1h': return '1h';
      case '4h': return '4h';
      case '6h': return '6h';
      case '12h': return '12h';
      case '1d': return '1d';
      case '1w': return '1w';
      default:
        // Binance klines offer no 3w/2M/3M/6M/1y interval, so those timeframes would
        // need client-side aggregation from a smaller one.
        console.warn(`Unsupported timeframe: ${tf}, defaulting to 1h`);
        return '1h';
    }
  }, []);

  // Rate limiting state. The timestamp is a ref because reading it from a memoised
  // callback's closure would always see the initial value and defeat the interval guard.
  const lastFetchTimeRef = useRef(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Rate limiting constants
  const MIN_FETCH_INTERVAL = 5000; // 5 seconds minimum between requests (increased from 2s)
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 10000; // 10 seconds delay between retries (increased from 5s)

  // Push a freshly fetched dataset into the series.
  //
  // A full reload (new symbol or timeframe) replaces the series and returns to the
  // default view. Otherwise only the bars from the previously-live bar onwards are
  // re-sent via update(), which finalises the bar that just closed and appends any new
  // ones while leaving the user's zoom and scroll position completely untouched.
  const applyDataset = useCallback((
    candles: CandlestickData[],
    greenVolume: LineData[],
    redVolume: LineData[],
    fullReload: boolean,
  ) => {
    const previous = chartDataRef.current;
    const previousLastTime = previous.length ? Number(previous[previous.length - 1].time) : 0;

    if (fullReload || previous.length === 0) {
      chartDataRef.current = candles;
      greenVolumeDataRef.current = greenVolume;
      redVolumeDataRef.current = redVolume;
    } else if (candles.length > 0) {
      // Merge rather than replace. A refresh only returns the most recent window, so
      // assigning it directly used to discard everything older — including any history
      // paged in by scrolling back — and silently shrink the chart to the last few hundred
      // bars. Bars at or after the window's start are superseded by the fresh copy.
      const firstNewTime = Number(candles[0].time);
      const keepOlder = <T extends { time: unknown }>(points: T[], incoming: T[]) => [
        ...points.filter((point) => Number(point.time) < firstNewTime),
        ...incoming
      ];

      chartDataRef.current = keepOlder(previous, candles);
      greenVolumeDataRef.current = keepOlder(greenVolumeDataRef.current, greenVolume);
      redVolumeDataRef.current = keepOlder(redVolumeDataRef.current, redVolume);
    }

    const series = candlestickSeriesRef.current;
    if (!series || candles.length === 0) return;

    const showVolume =
      chartTypeRef.current === 'candlestick' &&
      SHOW_VOLUME_BARS &&
      !!greenVolumeSeriesRef.current &&
      !!redVolumeSeriesRef.current;

    try {
      if (fullReload) {
        series.setData(toSeriesData(candles, chartTypeRef.current));
        if (showVolume) {
          greenVolumeSeriesRef.current!.setData(greenVolume);
          redVolumeSeriesRef.current!.setData(redVolume);
        }
        applyPriceScaleConfig();
        resetChart();
        return;
      }

      candles
        .filter((candle) => Number(candle.time) >= previousLastTime)
        .forEach((candle) => series.update(toSeriesPoint(candle, chartTypeRef.current)));

      if (showVolume) {
        greenVolume
          .filter((point) => Number(point.time) >= previousLastTime)
          .forEach((point) => greenVolumeSeriesRef.current!.update(point));
        redVolume
          .filter((point) => Number(point.time) >= previousLastTime)
          .forEach((point) => redVolumeSeriesRef.current!.update(point));
      }
    } catch (error) {
      console.warn('Error applying chart data:', error);
    }
  }, [applyPriceScaleConfig, resetChart]);

  // Fetch crypto data from Binance with rate limiting
  const fetchCryptoData = useCallback(async () => {
    const now = Date.now();
    
    // Prevent concurrent fetch calls
    if (fetchInProgressRef.current) {
      return;
    }
    
    // Rate limiting check. Switching symbol or timeframe must never be throttled,
    // otherwise the chart would be left showing the previous instrument's bars.
    const isNewDataset = loadedDatasetKeyRef.current !== `${symbol}|${timeframe}`;
    if (!isNewDataset && now - lastFetchTimeRef.current < MIN_FETCH_INTERVAL) {
      return;
    }
    
    // If rate limited, wait before retrying
    if (isRateLimited) {
      return;
    }
    
    // Mark fetch as in progress
    fetchInProgressRef.current = true;
    
    try {
      const binanceInterval = getBinanceInterval(timeframe);
      
      // For crypto symbols, use the symbol directly (already in USDT format)
      let symbolPair = symbol;
      if (isCrypto) {
        symbolPair = symbol;
      }
      
      
      const response = await fetch(`https://api.binance.com/api/v3/klines?symbol=${symbolPair}&interval=${binanceInterval}&limit=${HISTORY_PAGE_SIZE}`);
      
      if (!response.ok) {
        if (response.status === 429) {
          // Rate limited by Binance
          setIsRateLimited(true);
          setRetryCount(prev => prev + 1);
          console.warn('Binance API rate limited, will retry later');
          
          // Reset rate limit after longer delay for 429 errors
          setTimeout(() => {
            setIsRateLimited(false);
          }, RETRY_DELAY * 2); // Wait longer for 429 errors
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!Array.isArray(data)) {
        throw new Error(`Invalid response format: expected array, got ${typeof data}`);
      }

      const klines: BinanceKline[] = data;
      
      // Reset retry count on successful fetch
        setRetryCount(0);
        lastFetchTimeRef.current = now;
      
      if (klines.length > 0) {
        const latestKline = klines[klines.length - 1];
        const previousKline = klines[klines.length - 2];
        
        // Extract candlestick data
        const openPrice = parseFloat(latestKline[1]);
        const highPrice = parseFloat(latestKline[2]);
        const lowPrice = parseFloat(latestKline[3]);
        const closePrice = parseFloat(latestKline[4]);
        const volume = parseFloat(latestKline[5]);
        
        // Seed the displayed price from this snapshot only while the live stream is not
        // driving it. Doing it unconditionally made the header fight itself: the stream
        // publishes the traded price with the 24-hour change, whereas this path published a
        // bar close with the change measured against the previous bar, so both the price and
        // the percentage visibly jumped every time a refresh landed.
        if (previousKline && !liveStreamConnectedRef.current) {
          const previousClose = parseFloat(previousKline[4]);
          const change = closePrice - previousClose;
          const changePercent = previousClose > 0 ? (change / previousClose) * 100 : 0;
          const formattedChange = `${change >= 0 ? '+' : ''}${changePercent.toFixed(2)}%`;

          const formattedPrice = formatPriceWithAssetDecimals(closePrice, symbol);
          setCurrentPrice(formattedPrice);
          setPriceChange(formattedChange);
          setIsPositiveChange(change >= 0);
          setLastUpdateTime(new Date().toLocaleTimeString());

          // Update AI trading service with current price
          updatePriceForAI(symbol, formattedPrice);

          onPriceUpdate?.(formattedPrice, formattedChange, change >= 0);
        }
        
        // Call the kline price update callback
        const formattedPrice = formatPriceWithAssetDecimals(closePrice, symbol);
        onKlinePriceUpdate?.(formattedPrice, closePrice, volume);
        
        // Process chart data
        const processedChartData: CandlestickData[] = klines.map((kline) => ({
          time: (kline[0] / 1000) as UTCTimestamp,
          open: parseFloat(kline[1]),
          high: parseFloat(kline[2]),
          low: parseFloat(kline[3]),
          close: parseFloat(kline[4]),
        }));
        
        // Process volume data with color information
        const greenVolumeData: LineData[] = [];
        const redVolumeData: LineData[] = [];

        const datasetKey = `${symbol}|${timeframe}`;
        const fullReload = loadedDatasetKeyRef.current !== datasetKey;

        // Bar times repeat across symbols on the same timeframe, so a volume left behind by
        // the previous instrument would be read as this one's.
        if (fullReload) volumeByTimeRef.current.clear();
        
        klines.forEach((kline) => {
          const open = parseFloat(kline[1]);
          const close = parseFloat(kline[4]);
          const volume = parseFloat(kline[5]);
          const isGreen = close >= open;
          const barTime = (kline[0] / 1000) as UTCTimestamp;
          
          const volumePoint = {
            time: barTime,
            value: volume,
          };
          
          if (isGreen) {
            greenVolumeData.push(volumePoint);
          } else {
            redVolumeData.push(volumePoint);
          }

          volumeByTimeRef.current.set(Number(barTime), volume);
        });

        applyDataset(processedChartData, greenVolumeData, redVolumeData, fullReload);
        loadedDatasetKeyRef.current = datasetKey;
      }
      
      // Always update loading state when data is successfully fetched
      setIsLoading(false);
      setIsSymbolChanging(false);
      
      // Clear the loading timeout since we successfully fetched data
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      
      // Clear fetch in progress flag
      fetchInProgressRef.current = false;
      
      return data;
    } catch (err) {
      console.error('Error fetching crypto data:', err);
      
      // Handle different types of errors
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        console.warn('Network error or rate limiting detected');
        setIsRateLimited(true);
        
        // Only retry if we haven't exceeded max retries
        if (retryCount < MAX_RETRIES) {
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            setIsRateLimited(false);
          }, RETRY_DELAY * (retryCount + 1)); // Exponential backoff
        } else {
          console.error('Max retries exceeded, giving up');
          // Reset retry count after max retries to allow future attempts
          setTimeout(() => {
            setRetryCount(0);
            setIsRateLimited(false);
          }, RETRY_DELAY * 2); // Wait longer before allowing new attempts
        }
      }
      
      // Always update loading state even on error to prevent stuck loading state
      setIsLoading(false);
      setIsSymbolChanging(false);
      
      // Clear the loading timeout even on error
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      
      // Clear fetch in progress flag
      fetchInProgressRef.current = false;
      
      return [];
    }
  }, [symbol, timeframe, isCrypto, onPriceUpdate, onKlinePriceUpdate, getBinanceInterval, applyDataset]);

  // Page in older bars when the viewport approaches the oldest one loaded.
  //
  // Binance caps a klines request at 1000 bars, so the full history of an instrument can
  // only be read in blocks, walking backwards with endTime. Loading it all up front would
  // mean dozens of requests and tens of thousands of bars before the chart first painted;
  // fetching on demand is how TradingView behaves and keeps the initial load fast.
  const backfillOlderBars = useCallback(async () => {
    if (!isCrypto) return;

    const state = backfillStateRef.current;
    if (state.inFlight || state.exhausted) return;

    const chart = chartRef.current;
    const series = candlestickSeriesRef.current;
    const existing = chartDataRef.current;
    if (!chart || !series || existing.length === 0) return;

    const datasetKey = `${symbol}|${timeframe}`;
    const oldestSeconds = Number(existing[0].time);

    state.inFlight = true;
    setIsLoadingHistory(true);

    try {
      const binanceInterval = getBinanceInterval(timeframe);
      const response = await fetch(
        `https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=${binanceInterval}&endTime=${oldestSeconds * 1000 - 1}&limit=${HISTORY_PAGE_SIZE}`
      );

      if (!response.ok) return;

      const raw = await response.json();
      if (!Array.isArray(raw)) return;

      // The instrument's history predates nothing further back; stop asking.
      if (raw.length === 0) {
        state.exhausted = true;
        return;
      }

      // A short page means we have reached the listing date.
      if (raw.length < HISTORY_PAGE_SIZE) state.exhausted = true;

      // The symbol or timeframe changed while this was in flight, so these bars belong to
      // a chart that no longer exists.
      if (loadedDatasetKeyRef.current !== datasetKey) return;

      const older: CandlestickData[] = [];
      const olderGreenVolume: LineData[] = [];
      const olderRedVolume: LineData[] = [];

      (raw as BinanceKline[]).forEach((kline) => {
        const time = (kline[0] / 1000) as UTCTimestamp;
        if (Number(time) >= oldestSeconds) return; // Guard against an overlapping page.

        const open = parseFloat(kline[1]);
        const close = parseFloat(kline[4]);
        older.push({ time, open, high: parseFloat(kline[2]), low: parseFloat(kline[3]), close });

        const volume = parseFloat(kline[5]);
        const volumePoint = { time, value: volume };
        (close >= open ? olderGreenVolume : olderRedVolume).push(volumePoint);
        volumeByTimeRef.current.set(Number(time), volume);
      });

      if (older.length === 0) {
        state.exhausted = true;
        return;
      }

      // Prepending shifts every logical index along, so the viewport is moved by the same
      // amount. Without this the chart would appear to jump backwards in time the instant
      // new history arrived.
      const range = chart.timeScale().getVisibleLogicalRange();

      chartDataRef.current = [...older, ...chartDataRef.current];
      greenVolumeDataRef.current = [...olderGreenVolume, ...greenVolumeDataRef.current];
      redVolumeDataRef.current = [...olderRedVolume, ...redVolumeDataRef.current];

      // Older bars cannot be added with update(), which only accepts the newest bar.
      series.setData(toSeriesData(chartDataRef.current, chartTypeRef.current));

      if (
        chartTypeRef.current === 'candlestick' &&
        SHOW_VOLUME_BARS &&
        greenVolumeSeriesRef.current &&
        redVolumeSeriesRef.current
      ) {
        greenVolumeSeriesRef.current.setData(greenVolumeDataRef.current);
        redVolumeSeriesRef.current.setData(redVolumeDataRef.current);
      }

      if (range) {
        chart.timeScale().setVisibleLogicalRange({
          from: range.from + older.length,
          to: range.to + older.length
        });
      }
    } catch (error) {
      console.warn('Error loading older chart history:', error);
    } finally {
      state.inFlight = false;
      setIsLoadingHistory(false);
    }
  }, [isCrypto, symbol, timeframe, getBinanceInterval]);

  // Held in a ref so the chart-build effect can call it without listing it as a dependency,
  // which would rebuild the chart whenever the callback's identity changed.
  const backfillOlderBarsRef = useRef(backfillOlderBars);
  useEffect(() => {
    backfillOlderBarsRef.current = backfillOlderBars;
  }, [backfillOlderBars]);

  // Decimals for the price axis and the live price label. Without this every series was
  // formatted to 2 decimals, which rounds low-priced pairs into a flat line.
  const assetPrecision = useMemo(() => getAssetDecimalPlaces(symbol), [symbol]);
  const assetMinMove = useMemo(() => Number(`1e-${assetPrecision}`), [assetPrecision]);

  // Initialize lightweight-charts.
  //
  // This tears the chart down and rebuilds it, so it must only run for changes that
  // genuinely need a new chart or series (chart type, theme, symbol) — never for
  // incoming data. The visible range is carried across the rebuild so switching
  // between candlestick/line/area keeps you looking at the same bars.
  const initializeChart = useCallback(() => {
    if (!chartContainerRef.current) return;

    let preservedRange: { from: number; to: number } | null = null;

    // Clean up existing chart safely
    if (chartRef.current) {
      try {
        preservedRange = chartRef.current.timeScale().getVisibleLogicalRange() as
          | { from: number; to: number }
          | null;
      } catch (error) {
        preservedRange = null;
      }
      try {
        chartRef.current.remove();
      } catch (error) {
        console.warn('Error removing existing chart:', error);
      }
      chartRef.current = null;
      candlestickSeriesRef.current = null;
      greenVolumeSeriesRef.current = null;
      redVolumeSeriesRef.current = null;
    }

    // Clear container
    chartContainerRef.current.innerHTML = '';

    // Create new chart with theme-aware colors
    const chart = createChart(chartContainerRef.current, {
      // Let the chart track its own container. Opening or closing a side panel changes the
      // chart's width without any window resize, and this is the library's own
      // ResizeObserver-based sizing, so the canvas follows the container immediately
      // instead of staying at its old width and overflowing under the panel. The explicit
      // width/height below are only used as a fallback if ResizeObserver is unavailable.
      autoSize: true,
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
      layout: {
        background: { color: theme === 'dark' ? '#1e293b' : '#ffffff' },
        textColor: theme === 'dark' ? '#88909f' : '#6b7280', // More muted text color
      },
      localization: {
        locale: navigator.language,
        timeFormatter: formatCrosshairTime,
      },
      grid: {
        vertLines: { color: theme === 'dark' ? '#1e293b' : '#f9fafb', visible: false }, // Hide vertical grid lines
        horzLines: { color: theme === 'dark' ? '#1e293b' : '#f9fafb', visible: false }, // Hide horizontal grid lines
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: theme === 'dark' ? '#475569' : '#d1d5db', // More subtle crosshair color
          width: 1,
          style: 2,
        },
        horzLine: {
          color: theme === 'dark' ? '#475569' : '#d1d5db', // More subtle crosshair color
          width: 1,
          style: 2,
        },
      },
      rightPriceScale: {
        borderColor: theme === 'dark' ? '#334155' : '#d1d5db',
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
        // Track the bars in view, as TradingView does. Dragging the price axis still
        // switches the scale to manual mode until it is double-clicked to reset.
        autoScale: true,
        alignLabels: true,
        entireTextOnly: false,
        visible: true,
        ticksVisible: true,
        borderVisible: true,
      },
      timeScale: {
        borderColor: theme === 'dark' ? '#334155' : '#d1d5db',
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: formatAxisTickMark,
        rightOffset: DEFAULT_RIGHT_OFFSET,
        barSpacing: DEFAULT_BAR_SPACING,
        minBarSpacing: 0.1,
        lockVisibleTimeRangeOnResize: true,
        // TradingView keeps following the latest bar while you are parked at the right
        // edge, and leaves the view alone once you have scrolled back into history.
        rightBarStaysOnScroll: true,
        shiftVisibleRangeOnNewBar: true,
        fixLeftEdge: false,
        fixRightEdge: false,
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
        axisDoubleClickReset: true,
      },
      // Momentum when flicking the chart on touch devices.
      kineticScroll: {
        touch: true,
        mouse: false,
      },
    });

    // Create chart series based on chartType.
    // The last-value label and price line are shown on every series type so the live
    // price is always readable on the axis and moves with each incoming tick.
    let mainSeries: any = null;

    const priceFormat = {
      type: 'price' as const,
      precision: assetPrecision,
      minMove: assetMinMove,
    };

    switch (chartType) {
      case 'line':
        mainSeries = chart.addSeries(LineSeries, {
          color: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          lineWidth: 2,
          crosshairMarkerVisible: true,
          lastValueVisible: true,
          priceLineVisible: true,
          priceLineStyle: 2,
          priceFormat,
        });
        break;
        
      case 'area':
        mainSeries = chart.addSeries(AreaSeries, {
          lineColor: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          topColor: theme === 'dark' ? '#059669' : '#10b981',
          bottomColor: theme === 'dark' ? 'rgba(5, 150, 105, 0.1)' : 'rgba(16, 185, 129, 0.1)', // More muted area fill
          lineWidth: 2,
          crosshairMarkerVisible: true,
          lastValueVisible: true,
          priceLineVisible: true,
          priceLineStyle: 2,
          priceFormat,
        });
        break;
        
      case 'bar':
        mainSeries = chart.addSeries(HistogramSeries, {
          color: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          lastValueVisible: true,
          priceLineVisible: true,
          priceLineStyle: 2,
          priceFormat,
        });
        break;
        
      case 'histogram':
        mainSeries = chart.addSeries(HistogramSeries, {
          color: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          lastValueVisible: true,
          priceLineVisible: true,
          priceLineStyle: 2,
          priceFormat: {
            type: 'volume',
          },
        });
        break;
        
      case 'candlestick':
      default:
        mainSeries = chart.addSeries(CandlestickSeries, {
          upColor: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          downColor: theme === 'dark' ? '#dc2626' : '#ef4444', // More muted red
          borderDownColor: theme === 'dark' ? '#dc2626' : '#ef4444',
          borderUpColor: theme === 'dark' ? '#059669' : '#10b981',
          wickDownColor: theme === 'dark' ? '#dc2626' : '#ef4444',
          wickUpColor: theme === 'dark' ? '#059669' : '#10b981',
          // Enhanced styling for better visual appearance
          borderVisible: true,
          wickVisible: true,
          lastValueVisible: true,
          priceLineVisible: true,
          priceLineStyle: 2,
          priceFormat,
        });
        break;
    }

    // Create separate volume series for green and red volumes (only for candlestick)
    let greenVolumeSeries: any = null;
    let redVolumeSeries: any = null;
    
    if (chartType === 'candlestick' && SHOW_VOLUME_BARS) {
      greenVolumeSeries = chart.addSeries(HistogramSeries, {
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: '',
        lastValueVisible: false,
        priceLineVisible: false,
        color: 'rgba(38, 166, 154, 0.5)', // Green volume with 40% opacity
        baseLineWidth: 1, // Make volume bars very thin (histogram style)
        priceLineWidth: 1, // Thin outline
      });
      
      redVolumeSeries = chart.addSeries(HistogramSeries, {
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: '',
        lastValueVisible: false,
        priceLineVisible: false,
        color: 'rgba(240, 69, 66, 0.4)', // Red volume with 40% opacity
        baseLineWidth: 1, // Make volume bars very thin (histogram style)
        priceLineWidth: 1, // Thin outline
      });

      // Set volume series position and styling with theme-aware colors
      greenVolumeSeries.priceScale().applyOptions({
        scaleMargins: {
          top: 0.8,
          bottom: 0,
        },
        borderColor: theme === 'dark' ? '#334155' : '#d1d5db',
        textColor: theme === 'dark' ? '#e2e8f0' : '#1f2937',
      });
      
      redVolumeSeries.priceScale().applyOptions({
        scaleMargins: {
          top: 0.8,
          bottom: 0,
        },
        borderColor: theme === 'dark' ? '#334155' : '#d1d5db',
        textColor: theme === 'dark' ? '#e2e8f0' : '#1f2937',
      });

      // Apply histogram-style thin bar styling to volume series
      greenVolumeSeries.applyOptions({
        baseLineWidth: 0, // Very thin bars (histogram style)
        priceLineWidth: 1, // Thin outline
      });
      
      redVolumeSeries.applyOptions({
        baseLineWidth: 0, // Very thin bars (histogram style)
        priceLineWidth: 1, // Thin outline
      });
    }

    // Store references
    chartRef.current = chart;
    candlestickSeriesRef.current = mainSeries;
    setMainSeries(mainSeries); // Set the main series for position overlay
    setIsChartDisposed(false);
    greenVolumeSeriesRef.current = greenVolumeSeries;
    redVolumeSeriesRef.current = redVolumeSeries;

    // Seed the new series from the already-loaded bars, then put the viewport back where
    // it was so a chart-type or theme switch does not feel like a reload.
    if (chartDataRef.current.length > 0) {
      try {
        mainSeries.setData(toSeriesData(chartDataRef.current, chartType));

        // Set volume data only for candlestick charts
        if (chartType === 'candlestick' && SHOW_VOLUME_BARS && greenVolumeSeries && redVolumeSeries) {
          greenVolumeSeries.setData(greenVolumeDataRef.current);
          redVolumeSeries.setData(redVolumeDataRef.current);
        }

        if (preservedRange) {
          chart.timeScale().setVisibleLogicalRange(preservedRange);
        }
      } catch (error) {
        console.warn('Error setting initial chart data:', error);
      }
    }

    // Ask for older bars as the viewport nears the start of what is loaded. The callback is
    // reached through a ref so this effect does not have to depend on it.
    chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (!range) return;
      if (range.from < BACKFILL_TRIGGER_BARS) {
        void backfillOlderBarsRef.current();
      }

      // The logical range is measured in bar indices, so the newest bar is off to the right
      // whenever the range ends before the last one.
      const lastBarIndex = chartDataRef.current.length - 1;
      const scrolledBack = lastBarIndex > 0 && range.to < lastBarIndex;

      if (scrolledBack !== isScrolledBackRef.current) {
        isScrolledBackRef.current = scrolledBack;
        setIsScrolledBack(scrolledBack);
      }
    });

    // Which bar the OHLC readout describes. `param.time` is absent once the pointer leaves
    // the chart, which is the signal to go back to reporting the live candle. Only a ref is
    // written here: this fires on every pointer move.
    chart.subscribeCrosshairMove((param) => {
      hoveredBarTimeRef.current = param.time === undefined ? null : Number(param.time);
    });

    // Sizing is handled by the chart's own autoSize option, set above. A hand-rolled
    // ResizeObserver used to live here, writing width/height back into the chart on every
    // frame of the panels' 300ms width transition — which relaid out the canvas ~18 times
    // per toggle and could trip "ResizeObserver loop" warnings by mutating the DOM from
    // inside the observer callback.
    //
    // Panning, zooming and pinching are handled natively via the handleScroll/handleScale
    // options above. There is deliberately nothing here suppressing updates while the user
    // interacts: update() repaints the last bar without moving the viewport, so live ticks
    // and gestures no longer conflict.
  }, [chartType, theme, symbol, assetPrecision, assetMinMove]);

  // A new instrument or interval invalidates the loaded bars. Declared before the chart
  // build effect so they are dropped first and the new series is never seeded with the
  // previous dataset.
  useEffect(() => {
    chartDataRef.current = [];
    greenVolumeDataRef.current = [];
    redVolumeDataRef.current = [];
    loadedDatasetKeyRef.current = '';
    // The new instrument has its own history and its own listing date.
    backfillStateRef.current = { inFlight: false, exhausted: false };
    setIsLoadingHistory(false);
  }, [symbol, timeframe]);

  // Build the chart once per chart-type / theme / symbol change. Crucially this effect
  // must not depend on the bar data, or every tick would rebuild the chart and throw
  // away the user's zoom and scroll position.
  useEffect(() => {
    initializeChart();
  }, [initializeChart]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up chart instance
      if (chartRef.current) {
        try {
          chartRef.current.remove();
        } catch (error) {
          console.warn('Error removing chart on unmount:', error);
        }
        chartRef.current = null;
        candlestickSeriesRef.current = null;
        greenVolumeSeriesRef.current = null;
        redVolumeSeriesRef.current = null;
      }
      
      // Clean up loading timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      
      // Clean up hold timeout
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
        holdTimeoutRef.current = null;
      }
    };
  }, []);

  // Detect symbol changes and set loading state
  useEffect(() => {
    if (prevSymbol !== symbol && symbol) {
      // Always set loading state when symbol changes, regardless of current loading state
      setIsSymbolChanging(true);
      setIsLoading(true);
      setPrevSymbol(symbol);
      
      // Clear any existing timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      
      // Set a timeout to prevent loading state from getting stuck indefinitely
      loadingTimeoutRef.current = setTimeout(() => {
        console.warn(`Loading timeout for symbol ${symbol}, forcing loading state to false`);
        setIsLoading(false);
        setIsSymbolChanging(false);
        loadingTimeoutRef.current = null;
      }, 10000); // 10 second timeout
      
      // Clean up timeout when component unmounts or symbol changes again
      return () => {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
      };
    }
  }, [symbol, prevSymbol]);

  // Fetch data when component mounts or symbol changes
  useEffect(() => {
    if (isCrypto && !isRateLimited) {
      fetchCryptoData();
    } else if (!isCrypto) {
      setIsLoading(false);
      setIsSymbolChanging(false);
    }
  }, [symbol, isCrypto, fetchCryptoData, isRateLimited]);

  // A Firestore onSnapshot listener on the whole user document used to live here to watch
  // AI trading state, but its callback did nothing with the data — BackendAITradingContext
  // owns those updates. It was costing a document read on every write for no effect.

  // Real-time updates - much more conservative to prevent excessive API calls
  useEffect(() => {
    if (!isCrypto || isRateLimited) return;

    // Only a fallback. While the kline websocket is connected it already delivers
    // authoritative bars, so polling REST as well would just burn rate limit.
    if (isLiveStreamConnected) return;

    const intervalId = setInterval(() => {
      if (!isRateLimited && isCrypto) {
        fetchCryptoData();
      }
    }, 30000); // 30 seconds interval

    return () => clearInterval(intervalId);
  }, [isCrypto, isRateLimited, fetchCryptoData, isLiveStreamConnected]);

  // Write a bar into the series and keep the local dataset in step. Only ever calls
  // series.update(), which repaints the last bar in place and leaves zoom, pan and the
  // price scale untouched, and never touches React state, so no tick can trigger a
  // re-render or a chart rebuild.
  const commitBar = useCallback((candle: CandlestickData) => {
    const series = candlestickSeriesRef.current;
    if (!series) return;

    const data = chartDataRef.current;
    const time = Number(candle.time);
    const lastTime = data.length ? Number(data[data.length - 1].time) : -Infinity;

    if (time < lastTime) return; // Never rewrite history from the live stream.

    // Mutated in place rather than copied. This runs on every trade — tens of times a
    // second — and rebuilding a 500+ element array each time meant tens of thousands of
    // element copies per second feeding the garbage collector.
    if (time === lastTime) {
      data[data.length - 1] = candle;
    } else {
      data.push(candle);
      // Bound the dataset so a session left open for days cannot grow without limit.
      if (data.length > MAX_RETAINED_BARS) {
        data.splice(0, data.length - MAX_RETAINED_BARS);
      }
    }

    try {
      series.update(toSeriesPoint(candle, chartTypeRef.current));
    } catch (error) {
      // A stale timestamp can race the REST refresh; the next frame recovers.
      if (error instanceof Error && error.message?.includes('Cannot update oldest data')) {
        return;
      }
      console.warn('Error updating chart bar:', error);
    }
  }, []);

  // Binance's kline frame is authoritative: it carries the real open/high/low/close for
  // the period and opens the next bar itself, so there is no need to guess when a bar
  // rolls over.
  const applyKline = useCallback((kline: LiveKline) => {
    // The frame carries the bar's volume so far, which is what keeps the readout's volume
    // moving on the forming bar rather than sitting at whatever the last REST page said.
    if (Number.isFinite(kline.volume)) {
      volumeByTimeRef.current.set(Number(kline.time), kline.volume);
    }

    commitBar({
      time: kline.time as UTCTimestamp,
      open: kline.open,
      high: kline.high,
      low: kline.low,
      close: kline.close,
    });
  }, [commitBar]);

  // Kline frames only arrive about once a second. Individual trades arrive as they happen,
  // and are folded into the current bar in between so the candle and its price label move
  // genuinely tick by tick. A trade belonging to a later period is ignored: the next kline
  // frame is what legitimately opens the new bar.
  const applyTradeToBar = useCallback((price: number) => {
    const data = chartDataRef.current;
    if (data.length === 0 || !Number.isFinite(price)) return;

    const lastCandle = data[data.length - 1];
    const barSeconds = getTimeframeSeconds(timeframe);
    const nowSeconds = Math.floor(Date.now() / 1000);

    if (barSeconds && nowSeconds >= Number(lastCandle.time) + barSeconds) return;

    commitBar({
      time: lastCandle.time,
      open: lastCandle.open,
      high: Math.max(lastCandle.high, price),
      low: Math.min(lastCandle.low, price),
      close: price,
    });
  }, [commitBar, timeframe]);

  // Volatile callbacks are mirrored into refs so the subscriptions below depend only on
  // the symbol and interval. Previously the market-data getter changed identity on every
  // websocket batch, which tore down and rebuilt the subscription several times a second.
  const applyKlineRef = useRef(applyKline);
  applyKlineRef.current = applyKline;
  const applyTradeToBarRef = useRef(applyTradeToBar);
  applyTradeToBarRef.current = applyTradeToBar;
  const onPriceUpdateRef = useRef(onPriceUpdate);
  onPriceUpdateRef.current = onPriceUpdate;
  const updatePriceForAIRef = useRef(updatePriceForAI);
  updatePriceForAIRef.current = updatePriceForAI;

  const streamInterval = useMemo(
    () => (getTimeframeSeconds(timeframe) ? getBinanceInterval(timeframe) : null),
    [timeframe, getBinanceInterval],
  );

  // Live candles and tick-by-tick price for the symbol on screen.
  //
  // The chart is fed by Binance's own kline frames (authoritative OHLC, and they open each
  // new bar themselves) refined by individual trades in between, so the candle body, wick
  // and price label all move as each trade happens. This replaced sampling the
  // all-symbols 24-hour ticker once a second, which could not produce correct bars.
  useEffect(() => {
    if (!isCrypto || !streamInterval) return;

    let lastPriceRender = 0;
    // The candle itself is repainted on every trade. This only throttles the React state
    // behind the textual price readout, which fans out into several panels' render trees;
    // refreshing that a few times a second already reads as live.
    const PRICE_RENDER_THROTTLE_MS = 400;

    const publishPrice = (price: number, force = false) => {
      const now = Date.now();
      if (!force && now - lastPriceRender < PRICE_RENDER_THROTTLE_MS) return;
      lastPriceRender = now;

      const formatted = formatPriceWithAssetDecimals(price, symbol);

      // The traded price is authoritative for "what does this cost right now", but the
      // 24-hour change percentage only exists on the ticker feed, so it is read from there.
      const ticker = binanceService.getMarketData().get(symbol);
      const change = ticker?.change ?? '+0.00%';
      const isPositive = ticker?.isPositive ?? true;

      setCurrentPrice(formatted);
      setPriceChange(change);
      setIsPositiveChange(isPositive);
      setLastUpdateTime(new Date().toLocaleTimeString());
      updatePriceForAIRef.current(symbol, formatted);
      onPriceUpdateRef.current?.(formatted, change, isPositive);
    };

    const unsubscribe = liveChartStream.subscribe(symbol, streamInterval, {
      onKline: (kline) => {
        applyKlineRef.current(kline);
        publishPrice(kline.close);
        binanceService.applyLivePrice(symbol, kline.close);
      },
      onTrade: (trade) => {
        applyTradeToBarRef.current(trade.price);
        publishPrice(trade.price);
        // Publish into the shared market data as well, so the asset list, positions and
        // anything else reading MarketData track the same price as these candles instead of
        // lagging a second behind on the 24hr ticker feed.
        binanceService.applyLivePrice(symbol, trade.price);
      },
      onStatusChange: (connected) => {
        liveStreamConnectedRef.current = connected;
        setIsLiveStreamConnected(connected);
      },
    });

    const seed = liveChartStream.getLastPrice(symbol);
    if (seed !== undefined) publishPrice(seed, true);

    return () => {
      unsubscribe();
      liveStreamConnectedRef.current = false;
      setIsLiveStreamConnected(false);
    };
  }, [isCrypto, symbol, streamInterval]);

  return (
    <div 
      // Anything that wants to cover the chart exactly — currently the enlarged sentiment
      // meter, which lives in the right panel — portals in here rather than trying to work
      // out where the chart is from its own corner of the layout.
      id={CHART_OVERLAY_HOST_ID}
      className={`relative w-full h-full ${theme === 'light' ? 'bg-white border border-0 border-gray-100' : 'border-gray-700'}`}
    >
      {/* Always render the loader but control visibility with CSS */}
      <div 
        className={`absolute inset-0 flex items-center justify-center theme-bg-primary rounded-lg text-xs transition-opacity duration-500 z-10 ${showLoader ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        {showLoader && (
          <Loader 
            size="md" 
            title="" 
            subtitle={isSymbolChanging ? `Loading ${symbol} chart data...` : "Loading chart data..."} 
          />
        )}
      </div>
      
      {/* Older bars are fetched as the user scrolls back; this marks that wait without
          covering the chart the way the full-screen loader does. */}
      {isLoadingHistory && !showLoader && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 px-2 py-0.5 rounded-full text-[10px] theme-bg-quaternary theme-text-secondary border theme-border pointer-events-none">
          Loading history…
        </div>
      )}

      {/* Chart content - always rendered but hidden when loading */}
      <div className={`w-full h-full transition-opacity duration-500 ${showLoader ? 'opacity-0' : 'opacity-100'}`}>
        {/* Chart Container */}
        <div 
          ref={chartContainerRef} 
          className="w-full h-full candlestick-chart"
          style={{
            // Add CSS for rounded corners on candlesticks
            '--candlestick-border-radius': '2px',
            // Ensure proper interaction
            pointerEvents: 'auto',
            touchAction: 'pan-x pan-y',
          } as React.CSSProperties}
        />
        
        {/* Jump back to the newest candle, offered only while it is out of view. */}
        {isScrolledBack && !showLoader && (
          <button
            onClick={() => chartRef.current?.timeScale().scrollToRealTime()}
            title="Scroll to most recent candle"
            aria-label="Scroll to most recent candle"
            className="absolute bottom-8 right-16 z-20 flex items-center justify-center w-6 h-6 rounded-full border theme-border theme-bg-quaternary theme-text-secondary shadow hover:theme-text-primary transition-colors duration-200"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        {/* How long the bar being drawn has left, under the live price on the axis. */}
        {mainSeries && !isChartDisposed && !showLoader && (
          <ChartCandleCountdown series={mainSeries} candlesRef={chartDataRef} />
        )}

        {/* Position Manager */}
        {mainSeries && !isChartDisposed && (
          <ChartPositionManager
            seriesRef={mainSeries}
            symbol={symbol}
            currentPrice={parseFloat(currentPrice.replace(/[,$]/g, ''))}
            isVisible={showPositionOverlay && !showLoader}
          />
        )}

      </div>
      
      {/* Rate Limited Indicator */}
      {isRateLimited && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
          <div className="text-center text-white">
            <div className="text-sm font-medium mb-2">Rate Limited</div>
            <div className="text-xs opacity-75">Too many requests, retrying in a moment...</div>
            {retryCount > 0 && (
              <div className="text-xs opacity-50 mt-1">Attempt {retryCount}/{MAX_RETRIES}</div>
            )}
          </div>
        </div>
      )}
      
      {/* Price overlay */}
      <div className="absolute bottom-8 left-1 theme-bg-secondary bg-opacity-95 p-3 py-2 rounded-lg z-10 shadow-xs border theme-border">
        {showDateTime && lastUpdateTime && (
          <div className="text-xs theme-text-quaternary">
            {(() => {
              // Format today's date as "Today, 30th August, 2025"
              const now = new Date();
              const day = now.getDate();
              const monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
              ];
              // Get ordinal suffix for the day
              function getOrdinal(n: number) {
                if (n > 3 && n < 21) return n + "th";
                switch (n % 10) {
                  case 1: return n + "st";
                  case 2: return n + "nd";
                  case 3: return n + "rd";
                  default: return n + "th";
                }
              }
              const formattedDate = `Today, ${getOrdinal(day)} ${monthNames[now.getMonth()]}, ${now.getFullYear()}`;
              // Format time as "8:17:14 AM"
              const formattedTime = now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
              return (
                <>                
                  {formattedDate} at {lastUpdateTime}
                </>
              );
            })()}
            {/* {new Date().toLocaleDateString()} {lastUpdateTime} */}
          </div>
        )}
      </div>
      
      {/* Trading Buttons */}
      <div className="absolute top-2 left-2 z-20 flex gap-2">
        {/* AI Button Group */}
        <div className="flex">
          {/* AI Button */}
          
          <ChartTooltip
            label={
              isAILoading
                ? (isAIRunning ? 'Stopping AI…' : 'Starting AI…')
                : isAIRunning
                  ? 'Stop AI trading'
                  : ((aiState.canStart || ((aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled && (aiCurrentFirestoreStrategy as any)?.activeStrategyInstance !== null))
                      ? 'Start AI trading'
                      : 'Select an AI strategy')
            }
          >
          <button
            data-ai-button
            onClick={handleAIStartStop}
            disabled={isAILoading}
            className={`
              px-2 py-1 text-xs font-medium transition-colors duration-200 flex items-center gap-1
              ${(aiState.canStart || aiState.isRunning || ((aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled && (aiCurrentFirestoreStrategy as any)?.activeStrategyInstance !== null)) 
                ? 'rounded-l' 
                : 'rounded'
              }
              ${isAILoading 
                ? 'opacity-50 cursor-not-allowed' 
                : isAIRunning 
                  ? (theme === 'dark' 
                      ? 'bg-green-500/30 text-green-300 hover:bg-green-500/40' 
                      : 'bg-green-100 text-green-700 hover:bg-green-200')
                  : (aiState.canStart || (aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled)
                    ? (theme === 'dark' 
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                        : 'bg-green-50 text-green-600 hover:bg-green-100')
                    : (theme === 'dark' 
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                        : 'bg-green-50 text-green-600 hover:bg-green-100')
              }
            `}
          >
            {isAILoading ? (
              <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
            ) : (aiState.canStart || aiState.isRunning || ((aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled && (aiCurrentFirestoreStrategy as any)?.activeStrategyInstance !== null)) ? (
              isAIRunning ? (
                <Pause className="h-3 w-3" />
              ) : (
                <Play className="h-3 w-3" />
              )
            ) : null}
            AI
          </button>
          </ChartTooltip>

          {/* AI Settings Button - Only show when strategy is selected */}
          {(aiState.canStart || aiState.isRunning || ((aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled && (aiCurrentFirestoreStrategy as any)?.activeStrategyInstance !== null)) && (
            <ChartTooltip label="Strategy settings">
              <button
                onClick={handleStrategySettingsClick}
                disabled={isSettingsLoading}
                className={`
                  p-1 rounded-r transition-colors duration-200 flex items-center justify-center
                  ${isSettingsLoading 
                    ? 'opacity-50 cursor-not-allowed' 
                    : theme === 'dark' 
                      ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                      : 'bg-green-50 text-green-600 hover:bg-green-100'
                  }
                `}
              >
                {isSettingsLoading ? (
                  <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Settings className="h-3 w-3" />
                )}
              </button>
            </ChartTooltip>
          )}
        </div>

        {/* AI Strategy Display */}
        {(aiState.canStart || aiState.isRunning || (aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled) && (
          <div className={`
            px-2 py-1 text-xs font-medium rounded transition-colors duration-200
            ${theme === 'dark' 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-green-50 text-green-600 border border-green-200'
            }
          `}>
            {(aiCurrentFirestoreStrategy as any)?.aiSelectedStrategy || 
             (aiCurrentAssetConfig.strategy === 'stochastic' ? 'MiAI' :
              aiCurrentAssetConfig.strategy === 'supertrend' ? 'LazyB CrazyB' : 
              aiCurrentAssetConfig.strategy === 'bollingerbands' ? 'Huge Hug' :
              aiCurrentAssetConfig.strategy === 'rsi' ? 'AI Assistant' : 
              (aiCurrentFirestoreStrategy as any)?.name || 'LazyB CrazyB')}
            {isAIRunning && (
              <span className="ml-1 text-green-400">●</span>
            )}
          </div>
        )}
        
        {/* Buy Button with Hold Functionality */}
        <div className="flex items-center space-x-1">
          <ChartTooltip
            label={showBuyInput
              ? (buyOrderType === 'stop' ? 'Buy stop order' : 'Buy limit order')
              : 'Buy at market — hold for a limit order'}
          >
          <button
            onClick={handleBuyClick}
            onMouseDown={handleBuyHold}
            onMouseUp={handleButtonRelease}
            onMouseLeave={handleButtonRelease}
            onTouchStart={handleBuyHold}
            onTouchEnd={handleButtonRelease}
            disabled={isBuyLoading}
            className={`
              px-2 py-1 text-xs font-medium rounded transition-colors duration-200 flex items-center space-x-1
              ${isHolding 
                ? 'scale-95' 
                : ''
              }
              ${isBuyLoading 
                ? 'opacity-50 cursor-not-allowed' 
                : theme === 'dark' 
                  ? 'bg-[#2AB7A6]/20 text-[#2AB7A6] hover:bg-[#2AB7A6]/30' 
                  : 'bg-[#2AB7A6]/10 text-[#2AB7A6] hover:bg-[#2AB7A6]/20'
              }
            `}
          >
            {isBuyLoading ? (
              <>
                <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
                <span>Buying...</span>
              </>
            ) : (
              'Buy'
            )}
          </button>
          </ChartTooltip>
          
          {/* Buy Price Input - appears when hold is triggered */}
          {showBuyInput && (
            <>
              <NumberInput
                value={buyPrice}
                onChange={(e) => setBuyPrice(e.target.value)}
                className="w-20"
                size="sm"
                placeholder="Price"
                step="0.00001"
                autoFocus
              />
              {/* Order Type Indicator */}
              {buyOrderType && (
                <div className={`
                  px-2 py-1 text-xs font-medium rounded
                  ${buyOrderType === 'stop' 
                    ? theme === 'dark' 
                      ? 'bg-orange-500/20 text-orange-400' 
                      : 'bg-orange-100 text-orange-600'
                    : theme === 'dark'
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-blue-100 text-blue-600'
                  }
                `}>
                  {buyOrderType === 'stop' ? 'STOP' : 'LIMIT'}
                </div>
              )}
              <button
                onClick={closeBuyInput}
                className={`
                  p-1 rounded transition-colors duration-200
                  ${theme === 'dark'
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }
                `}
                title="Close"
              >
                <X className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
        
        {/* Sell Button with Hold Functionality */}
        <div className="flex items-center space-x-1">
          <ChartTooltip
            label={showSellInput
              ? (sellOrderType === 'stop' ? 'Sell stop order' : 'Sell limit order')
              : 'Sell at market — hold for a limit order'}
          >
          <button
            onClick={handleSellClick}
            onMouseDown={handleSellHold}
            onMouseUp={handleButtonRelease}
            onMouseLeave={handleButtonRelease}
            onTouchStart={handleSellHold}
            onTouchEnd={handleButtonRelease}
            disabled={isSellLoading}
            className={`
              px-2 py-1 text-xs font-medium rounded transition-colors duration-200 flex items-center space-x-1
              ${isHolding 
                ? 'scale-95' 
                : ''
              }
              ${isSellLoading 
                ? 'opacity-50 cursor-not-allowed' 
                : theme === 'dark' 
                  ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                  : 'bg-red-50 text-red-600 hover:bg-red-100'
              }
            `}
          >
            {isSellLoading ? (
              <>
                <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
                <span>Selling...</span>
              </>
            ) : (
              'Sell'
            )}
          </button>
          </ChartTooltip>
          
          {/* Sell Price Input - appears when hold is triggered */}
          {showSellInput && (
            <>
              <NumberInput
                value={sellPrice}
                onChange={(e) => setSellPrice(e.target.value)}
                className="w-20"
                size="sm"
                placeholder="Price"
                step="0.00001"
                autoFocus
              />
              {/* Order Type Indicator */}
              {sellOrderType && (
                <div className={`
                  px-2 py-1 text-xs font-medium rounded
                  ${sellOrderType === 'stop' 
                    ? theme === 'dark' 
                      ? 'bg-orange-500/20 text-orange-400' 
                      : 'bg-orange-100 text-orange-600'
                    : theme === 'dark'
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-blue-100 text-blue-600'
                  }
                `}>
                  {sellOrderType === 'stop' ? 'STOP' : 'LIMIT'}
                </div>
              )}
              <button
                onClick={closeSellInput}
                className={`
                  p-1 rounded transition-colors duration-200
                  ${theme === 'dark'
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }
                `}
                title="Close"
              >
                <X className="h-3 w-3" />
              </button>
            </>
          )}
        </div>

        {/* The bar under the crosshair, or the live one when the pointer is elsewhere. */}
        <div className="hidden md:flex items-center">
          <ChartOhlcReadout
            symbol={symbol}
            candlesRef={chartDataRef}
            volumesRef={volumeByTimeRef}
            hoveredTimeRef={hoveredBarTimeRef}
          />
        </div>
        
        {/* Position Overlay Toggle Button - Only show when there are positions */}
        {chartPositions.filter(pos => pos.symbol === symbol && pos.isVisible !== false).length > 0 && (
          <ChartTooltip label={showPositionOverlay ? 'Hide position overlay' : 'Show position overlay'}>
            <button
              onClick={() => setShowPositionOverlay(!showPositionOverlay)}
              className={`
                px-2 py-1 text-xs font-medium rounded transition-colors duration-200 flex items-center justify-center
                ${theme === 'dark' 
                  ? 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }
              `}
            >
              {showPositionOverlay ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </ChartTooltip>
        )}

        {/* Reset the view: default zoom, back to the latest bar, price scale auto again. */}
        <ChartTooltip label="Reset chart">
          <button
            onClick={resetChart}
            className={`
              px-2 py-1 text-xs font-medium rounded transition-colors duration-200 flex items-center justify-center
              ${theme === 'dark'
                ? 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }
            `}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </ChartTooltip>
      </div>
      
      {/* Charting Tools - Left Side 
      <ChartingTools
        onToolSelect={handleDrawingToolSelect}
        selectedTool={selectedDrawingTool}
        onDrawingComplete={handleDrawingComplete}
        onClearAll={handleClearAllDrawings}
        isVisible={!showLoader}
        chartContainerRef={chartContainerRef}
      />
      */}
      {/* AI Trading Settings Popup */}
      <AITradingSettings
        isOpen={showAISettings}
        onClose={() => setShowAISettings(false)}
        symbol={symbol}
        currentPrice={currentPrice}
        selectedStrategy={
          // Use new per-asset system first, fallback to legacy system (same as AITradePanel)
          assetAIConfig?.activeStrategyInstance?.type ||
          assetAIConfig?.activeStrategyInstance?.id ||
          (aiCurrentFirestoreStrategy as any)?.type ||
          (aiCurrentFirestoreStrategy as any)?.id ||
          aiCurrentAssetConfig.strategy ||
          ''
        }
        onStrategyChange={handleStrategyChange}
        onConfigUpdate={handleConfigUpdate}
        onSignalUpdate={handleSignalUpdate}
        isRunning={aiState.isRunning || aiCurrentAssetConfig.isRunning}
      />

      {/* Trading Error Modal */}
      <TradingErrorModal
        isOpen={!!tradingError}
        onClose={hideError}
        onRetry={retryOrder}
        error={tradingError || { type: 'unknown', message: '' }}
        orderDetails={tradingError?.orderDetails}
      />

      
      {/* Centered Watermark Logo - Theme Aware */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 opacity-60" style={{ top: '-130px' }}>
        <div className={`transform scale-100 animate-spin-slow ${theme === 'dark' ? 'opacity-20' : 'opacity-10'}`}>
          {/* Theme-aware logo - black for dark theme, white for light theme */}
          {theme === 'dark' ? (
            <img 
              src="/images/paul_d_black.svg"
              alt="Live Trading by Paul D" 
              className="w-48 sm:w-64 md:w-80 lg:w-96 xl:w-[350px] h-auto filter drop-shadow-2xl"
            />
          ) : (
            <img 
              src="/images/paul_d_white.svg"
              alt="Live Trading by Paul D" 
              className="w-48 sm:w-64 md:w-80 lg:w-96 xl:w-[350px] h-auto filter drop-shadow-2xl"
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Memoised because this is by far the heaviest component in the tree and the layout above
// it re-renders on every price tick. Without this, an unrelated parent state change costs a
// full re-render of the chart and all of its hooks.
export default React.memo(TradingChart);
